import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { ComponentSync, LL_H_C, LL_V_L, ModuleFE_Thunderstorm, TS_Checkbox, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { generateNewFHIRResourceSubType } from '@app/fhir-shared';
import { Component_EditorHeader, FadeScrollWrapper } from '@app/styles';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { SearchableItem_FHIRResource } from '../consts.js';
import './Editor_FHIRResource.scss';
import { __stringify, asArray, MimeType_json, sortArray } from '@nu-art/ts-common';
import { DefaultEditor_InputText } from '@app/styles/frontend/ui-manager/ui-components';
import { ICONS } from '@app/styles';
import { ModuleFE_FHIRResourceSubtype } from '../../../_entity.js';
import { Editor_FHIRResourceSubType } from '../Editor_FHIRResourceSubType/Editor_FHIRResourceSubType.js';
const viewMode_View = 'view';
const viewMode_Edit = 'edit';
export class Editor_FHIRResource extends ComponentSync {
    scrollToSubTypeId;
    // ######################## Lifecycle ########################
    __onFHIRResourceSubtypesUpdated = (...params) => {
        const subTypes = asArray(params[1]);
        if (subTypes.some(st => st.resourceId === this.state.editable.item._id))
            this.forceUpdate();
    };
    deriveStateFromProps(nextProps, state) {
        state.editable = nextProps.editable;
        state.editMode = nextProps.editMode ?? (!state.editable.item._id ? true : (this.state?.editMode ?? false));
        state.subtypeScrollRef ??= React.createRef();
        this.logInfo(state);
        return state;
    }
    // ######################## Logic ########################
    getItemActions = () => {
        return [{
                label: 'Edit Resource',
                action: async () => this.setState({ editMode: true }),
            }, {
                label: 'Delete Resource',
                action: async () => {
                    await this.state.editable.delete();
                    this.props.deselectResource?.();
                }
            }, {
                label: 'Export JSON',
                action: async () => {
                    const item = this.state.editable.item;
                    ModuleFE_Thunderstorm.downloadFile({
                        fileName: `FHIR Resource - ${item.label}`,
                        mimeType: MimeType_json,
                        content: __stringify(item, true)
                    });
                }
            }];
    };
    getEditModeActions = () => {
        const actions = [];
        //Actions for existing item
        if (this.state.editable.item._id)
            actions.push({
                label: 'Done',
                action: async () => this.setState({ editMode: false }),
            });
        //Actions for new item
        else {
            if (this.props.deselectResource)
                actions.push({
                    label: 'Cancel',
                    action: async () => {
                        return this.props.deselectResource();
                    },
                });
            actions.push({
                label: 'Save',
                action: async () => {
                    await this.state.editable.save();
                },
            });
        }
        return actions;
    };
    getViewMode = () => this.state.editMode ? viewMode_Edit : viewMode_View;
    createNewSubType = async () => {
        const resource = this.state.editable.item;
        const newSubType = generateNewFHIRResourceSubType(resource);
        try {
            const subType = await ModuleFE_FHIRResourceSubtype.v1.upsert(newSubType).executeSync();
            this.scrollToSubTypeId = subType._id;
            this.forceUpdate();
            setTimeout(() => {
                delete this.scrollToSubTypeId;
                this.forceUpdate();
            }, 0);
        }
        catch (err) {
            this.logError(err);
        }
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { id: 'editor__fhir-resource', children: [!this.props.hideHeader && this.renderHeader(), this.renderBody()] });
    }
    renderHeader = () => {
        return _jsx(Component_EditorHeader, { editable: this.state.editable, searchItem: SearchableItem_FHIRResource, editMode: this.state.editMode, itemActions: this.getItemActions(), editModeActions: this.getEditModeActions() });
    };
    renderBody = () => {
        return _jsxs(LL_H_C, { className: 'editor__fhir-resource__body', children: [_jsxs(LL_V_L, { className: 'editor__fhir-resource__body__column', children: [this.renderEditor_Label(), this.renderEditor_URLPrefix(), this.renderEditor_GroupParams()] }), _jsx(LL_V_L, { className: 'editor__fhir-resource__body__column', children: this.renderEditor_Subtypes() })] });
    };
    // ######################## Render - Editors ########################
    renderEditor_Label = () => {
        const resource = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Label', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-resource__body__value', children: resource.item.label }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: resource, prop: 'label' }) }
                ] }) });
    };
    renderEditor_URLPrefix = () => {
        const resource = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'URL Prefix', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-resource__body__value', children: resource.item.urlPrefix }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: resource, prop: 'urlPrefix' }) }
                ] }) });
    };
    renderEditor_GroupParams = () => {
        const resource = this.state.editable;
        const params = resource.editProp('groupParams', []);
        return _jsxs(TS_PropRenderer.Vertical, { label: this.renderEditor_GroupParams_Header(params), className: 'editor__fhir-resource__group-params', children: [!params.item.length && _jsx("div", { className: 'editor__fhir-resource__body__value', children: "No group params defined" }), _jsx(FadeScrollWrapper, { children: params.item.map((param, index) => {
                        const editable = params.editProp(index, {});
                        editable.setOnDelete(() => params.removeArrayItem(index));
                        return this.renderEditor_GroupParams_Param(editable);
                    }) })] });
    };
    renderEditor_GroupParams_Header = (params) => {
        return _jsxs(_Fragment, { children: ["Group Params", this.state.editMode && _jsx(ICONS.advisor_avn_add.component, { onClick: () => {
                        const newGroupParam = { param: 'new-group-param', multiSelection: false };
                        return params.updateArrayAt(newGroupParam);
                    } })] });
    };
    renderEditor_GroupParams_Param = (param) => {
        return _jsxs("div", { className: 'editor__fhir-resource__group-param', children: [_jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                        { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-resource__body__value', children: param.item.param }) },
                        { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: param, prop: 'param' }) }
                    ] }), _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                        {
                            key: viewMode_View, renderer: () => {
                                if (!param.item.multiSelection)
                                    return;
                                return _jsx("div", { className: 'editor__fhir-resource__body__value', children: "(can multi-select)" });
                            }
                        },
                        {
                            key: viewMode_Edit,
                            renderer: () => _jsx(TS_Checkbox, { icon: _jsx(ICONS.v.component, {}), checked: param.item.multiSelection, onCheck: () => param.updateObj({ multiSelection: !param.item.multiSelection }), children: "Can Multiselect" })
                        }
                    ] }), _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                        { key: viewMode_View, renderer: () => _jsx(_Fragment, {}) },
                        { key: viewMode_Edit, renderer: () => _jsx(ICONS.bin.component, { onClick: () => param.delete() }) }
                    ] })] }, param.item.param);
    };
    renderEditor_Subtypes = () => {
        const subTypes = ModuleFE_FHIRResourceSubtype.cache.filter(st => st.resourceId === this.state.editable.item._id);
        sortArray(subTypes, st => st.label);
        return _jsx(TS_PropRenderer.Vertical, { label: this.renderEditor_Subtypes_Label, className: 'editor__fhir-resource__sub-types', children: _jsx(FadeScrollWrapper, { scrollRef: this.state.subtypeScrollRef, children: subTypes.map((subType) => {
                    const triggerScroll = this.scrollToSubTypeId === subType._id;
                    return _jsx(Editor_FHIRResourceSubType, { subType: subType, editMode: this.state.editMode, subTypeScrollRef: this.state.subtypeScrollRef, triggerScroll: triggerScroll }, subType._id);
                }) }) });
    };
    renderEditor_Subtypes_Label = () => {
        const resource = this.state.editable.item;
        return _jsxs(_Fragment, { children: ["Sub Types", this.state.editMode && resource._id && _jsx(ICONS.advisor_avn_add.component, { onClick: this.createNewSubType })] });
    };
}
//# sourceMappingURL=Editor_FHIRResource.js.map